import { supabase } from '../supabase';
import { Tables } from '@/types/database.types';
import { handleError } from './db-utils';
import { getProjectById } from './projects';

interface CreateKpiOptions {
  name: string;
  description?: string | null;
  target?: number | null;
  currentValue?: number | null;
  unit?: string | null;
  category?: string | null;
}

interface UpdateKpiOptions {
  name?: string;
  description?: string | null;
  target?: number | null;
  currentValue?: number | null;
  unit?: string | null;
  category?: string | null;
}

export const getProjectKpis = async (projectId: string): Promise<Tables<'kpis'>[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('kpis')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });

    if (error) handleError(error, 'fetch kpis');
    return data || [];
  } catch (error) {
    handleError(error, 'fetch kpis');
    return [];
  }
};

export const createKpi = async (
  projectId: string,
  options: CreateKpiOptions
): Promise<Tables<'kpis'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('kpis')
      .insert({
        project_id: projectId,
        name: options.name,
        description: options.description ?? null,
        target: options.target ?? null,
        current_value: options.currentValue ?? 0,
        unit: options.unit ?? null,
        category: options.category ?? null,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) handleError(error, 'create kpi');
    if (!data) throw new Error('No data returned from create');
    return data;
  } catch (error) {
    handleError(error, 'create kpi');
    throw error;
  }
};

export const updateKpi = async (
  kpiId: string,
  options: UpdateKpiOptions
): Promise<Tables<'kpis'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (options.name !== undefined) updateData.name = options.name;
    if (options.description !== undefined) updateData.description = options.description;
    if (options.target !== undefined) updateData.target = options.target;
    if (options.currentValue !== undefined) updateData.current_value = options.currentValue;
    if (options.unit !== undefined) updateData.unit = options.unit;
    if (options.category !== undefined) updateData.category = options.category;

    const { data, error } = await supabase
      .from('kpis')
      .update(updateData)
      .eq('id', kpiId)
      .select()
      .single();

    if (error) handleError(error, 'update kpi');
    if (!data) throw new Error('No data returned from update');

    // Append a history row whenever the current value moves so the
    // dashboard sparkline + trend reflect the change. Caller is
    // responsible for not invoking updateKpi redundantly with the same
    // value (autosave debouncing already takes care of this in the UI).
    // Non-fatal: a history-insert failure shouldn't break the primary
    // update — log and continue.
    if (options.currentValue !== undefined && options.currentValue !== null) {
      try {
        await recordKpiHistory(kpiId, options.currentValue);
      } catch (historyErr) {
        console.warn('Failed to append kpi_history row:', historyErr);
      }
    }

    return data;
  } catch (error) {
    handleError(error, 'update kpi');
    throw error;
  }
};

/**
 * Append a kpi_history row. Called automatically by updateKpi when
 * current_value changes; can also be called directly to backfill or to
 * record a measurement without otherwise editing the kpi row. The
 * dashboard renders sparklines from this table.
 */
export const recordKpiHistory = async (
  kpiId: string,
  value: number,
  recordedAt?: string
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('kpi_history')
      .insert({
        kpi_id: kpiId,
        value,
        recorded_at: recordedAt ?? new Date().toISOString(),
        created_by: user.id,
      });

    if (error) handleError(error, 'record kpi history');
  } catch (error) {
    handleError(error, 'record kpi history');
  }
};

/**
 * Fetch the recorded history for all KPIs in a project. Returns a Map
 * keyed by kpi_id with values sorted oldest → newest, suitable for
 * passing straight to a sparkline. Limited to the most recent 30
 * points per kpi to keep payloads bounded; widen if we need longer
 * trend windows.
 */
export interface KpiHistoryRow {
  id: string;
  value: number;
  recorded_at: string;
}

export const getProjectKpiHistory = async (
  projectId: string
): Promise<Map<string, KpiHistoryRow[]>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Pull all history rows joined via kpi_id → kpis to filter by project.
    // Could be a single JOIN query; using the in-clause keeps RLS simple
    // (each row filtered by its kpi's project membership independently).
    const { data: kpiRows, error: kpiErr } = await supabase
      .from('kpis')
      .select('id')
      .eq('project_id', projectId);
    if (kpiErr) handleError(kpiErr, 'fetch kpis for history');
    const kpiIds = (kpiRows ?? []).map((k) => k.id);
    if (kpiIds.length === 0) return new Map();

    const { data, error } = await supabase
      .from('kpi_history')
      .select('id, kpi_id, value, recorded_at')
      .in('kpi_id', kpiIds)
      .order('recorded_at', { ascending: true });
    if (error) handleError(error, 'fetch kpi history');

    const grouped = new Map<string, KpiHistoryRow[]>();
    for (const row of data ?? []) {
      const arr = grouped.get(row.kpi_id) ?? [];
      arr.push({ id: row.id, value: row.value, recorded_at: row.recorded_at });
      grouped.set(row.kpi_id, arr);
    }
    // Cap each series at the most recent 30 points.
    for (const [kpiId, arr] of grouped) {
      if (arr.length > 30) grouped.set(kpiId, arr.slice(-30));
    }
    return grouped;
  } catch (error) {
    handleError(error, 'fetch kpi history');
    return new Map();
  }
};

/**
 * Update a single recorded measurement. Either value or recorded_at (or
 * both) can be changed. Returns the updated row.
 */
export const updateKpiHistoryPoint = async (
  historyId: string,
  updates: { value?: number; recordedAt?: string },
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const patch: Record<string, unknown> = {};
    if (updates.value !== undefined) patch.value = updates.value;
    if (updates.recordedAt !== undefined) patch.recorded_at = updates.recordedAt;
    if (Object.keys(patch).length === 0) return;

    const { error } = await supabase
      .from('kpi_history')
      .update(patch)
      .eq('id', historyId);
    if (error) handleError(error, 'update kpi history point');
  } catch (error) {
    handleError(error, 'update kpi history point');
    throw error;
  }
};

/**
 * Delete a single recorded measurement.
 */
export const deleteKpiHistoryPoint = async (historyId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('kpi_history')
      .delete()
      .eq('id', historyId);
    if (error) handleError(error, 'delete kpi history point');
  } catch (error) {
    handleError(error, 'delete kpi history point');
    throw error;
  }
};

export const deleteKpi = async (kpiId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('kpis')
      .delete()
      .eq('id', kpiId);

    if (error) handleError(error, 'delete kpi');
  } catch (error) {
    handleError(error, 'delete kpi');
    throw error;
  }
};
